import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Space,
  Select,
  DatePicker,
  Input,
  Button,
  Tag,
  Typography,
  message,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  ClearOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { apiClient } from '../services/api.ts';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface ProductMovement {
  id: number;
  movement_date: string;
  movement_type: string;
  quantity: number;
  product_id: number;
  product_code: string;
  product_description: string;
  warehouse_id: number | null;
  warehouse_name: string | null;
  from_udc_pallet: string | null;
  from_udc_collo: string | null;
  to_udc_pallet: string | null;
  to_udc_collo: string | null;
  reason: string;
  notes: string | null;
  performed_by: number | null;
  user_name: string | null;
  created_at: string;
}

interface PaginationData {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

const ProductMovements: React.FC = () => {
  const [movements, setMovements] = useState<ProductMovement[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 0
  });

  // Filtri
  const [filters, setFilters] = useState<any>({
    movement_type: null,
    product_id: null,
    warehouse_id: null,
    user_id: null,
    start_date: null,
    end_date: null
  });

  // Opzioni per i filtri
  const [filterOptions, setFilterOptions] = useState<any>({
    products: [],
    warehouses: [],
    users: []
  });

  // Carica opzioni filtri
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Carica movimenti al mount e quando cambiano filtri/paginazione
  useEffect(() => {
    loadMovements();
  }, [pagination.page, pagination.page_size, filters]);

  const loadFilterOptions = async () => {
    try {
      // Carica prodotti
      const productsRes = await apiClient.get('/products');
      // Carica magazzini
      const warehousesRes = await apiClient.get('/warehouses');
      // Carica utenti
      const usersRes = await apiClient.get('/users');

      setFilterOptions({
        products: productsRes.data,
        warehouses: warehousesRes.data,
        users: usersRes.data
      });
    } catch (error) {
      console.error('Errore caricamento opzioni filtri:', error);
    }
  };

  const loadMovements = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        page_size: pagination.page_size
      };

      // Aggiungi filtri attivi
      if (filters.movement_type) params.movement_type = filters.movement_type;
      if (filters.product_id) params.product_id = filters.product_id;
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await apiClient.get('/product-movements', { params });

      setMovements(response.data.movements);
      setPagination(response.data.pagination);
    } catch (error: any) {
      console.error('Errore caricamento movimenti:', error);
      message.error('Errore nel caricamento dei movimenti');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 }); // Reset a pagina 1
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters({
        ...filters,
        start_date: dates[0].format('YYYY-MM-DD'),
        end_date: dates[1].format('YYYY-MM-DD')
      });
    } else {
      setFilters({
        ...filters,
        start_date: null,
        end_date: null
      });
    }
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearFilters = () => {
    setFilters({
      movement_type: null,
      product_id: null,
      warehouse_id: null,
      user_id: null,
      start_date: null,
      end_date: null
    });
    setPagination({ ...pagination, page: 1 });
  };

  const getMovementTypeColor = (type: string) => {
    switch (type) {
      case 'IN':
        return 'green';
      case 'OUT':
        return 'red';
      case 'TRANSFER':
        return 'blue';
      case 'ADJUSTMENT':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getMovementTypeLabel = (type: string) => {
    switch (type) {
      case 'IN':
        return 'Carico';
      case 'OUT':
        return 'Scarico';
      case 'TRANSFER':
        return 'Trasferimento';
      case 'ADJUSTMENT':
        return 'Aggiustamento';
      default:
        return type;
    }
  };

  const columns: ColumnsType<ProductMovement> = [
    {
      title: 'Data',
      dataIndex: 'movement_date',
      key: 'movement_date',
      width: 120,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Tipo',
      dataIndex: 'movement_type',
      key: 'movement_type',
      width: 120,
      render: (type: string) => (
        <Tag color={getMovementTypeColor(type)}>
          {getMovementTypeLabel(type)}
        </Tag>
      )
    },
    {
      title: 'Prodotto',
      key: 'product',
      width: 250,
      render: (record: ProductMovement) => (
        <div>
          <div><strong>{record.product_code}</strong></div>
          <div style={{ fontSize: '12px', color: '#888' }}>{record.product_description}</div>
        </div>
      )
    },
    {
      title: 'Quantità',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right',
      render: (qty: number) => (
        <Tag color={qty >= 0 ? 'green' : 'red'}>
          {qty >= 0 ? '+' : ''}{qty}
        </Tag>
      )
    },
    {
      title: 'Magazzino',
      dataIndex: 'warehouse_name',
      key: 'warehouse_name',
      width: 150,
      render: (name: string | null) => name || '-'
    },
    {
      title: 'UDC Origine',
      key: 'from_udc',
      width: 150,
      render: (record: ProductMovement) => {
        if (!record.from_udc_pallet && !record.from_udc_collo) return '-';
        return (
          <div style={{ fontSize: '12px' }}>
            {record.from_udc_pallet && <div>P: {record.from_udc_pallet}</div>}
            {record.from_udc_collo && <div>C: {record.from_udc_collo}</div>}
          </div>
        );
      }
    },
    {
      title: 'UDC Destinazione',
      key: 'to_udc',
      width: 150,
      render: (record: ProductMovement) => {
        if (!record.to_udc_pallet && !record.to_udc_collo) return '-';
        return (
          <div style={{ fontSize: '12px' }}>
            {record.to_udc_pallet && <div>P: {record.to_udc_pallet}</div>}
            {record.to_udc_collo && <div>C: {record.to_udc_collo}</div>}
          </div>
        );
      }
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      key: 'reason',
      width: 200
    },
    {
      title: 'Note',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      render: (notes: string | null) => notes || '-'
    },
    {
      title: 'Utente',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 120,
      render: (name: string | null) => name || '-'
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>📊 Storia Movimenti Prodotti</Title>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Totale Movimenti"
              value={pagination.total}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Pagina Corrente"
              value={`${pagination.page} / ${pagination.total_pages}`}
            />
          </Col>
        </Row>
      </Card>

      <Card title="🔍 Filtri" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Select
              placeholder="Tipo Movimento"
              allowClear
              style={{ width: '100%' }}
              value={filters.movement_type}
              onChange={(value) => handleFilterChange('movement_type', value)}
            >
              <Option value="IN">Carico</Option>
              <Option value="OUT">Scarico</Option>
              <Option value="TRANSFER">Trasferimento</Option>
              <Option value="ADJUSTMENT">Aggiustamento</Option>
            </Select>
          </Col>

          <Col span={6}>
            <Select
              showSearch
              placeholder="Prodotto"
              allowClear
              style={{ width: '100%' }}
              value={filters.product_id}
              onChange={(value) => handleFilterChange('product_id', value)}
              filterOption={(input, option: any) =>
                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {filterOptions.products.map((product: any) => (
                <Option key={product.id} value={product.id}>
                  {`${product.internal_product_code} - ${product.product_description}`}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={6}>
            <Select
              placeholder="Magazzino"
              allowClear
              style={{ width: '100%' }}
              value={filters.warehouse_id}
              onChange={(value) => handleFilterChange('warehouse_id', value)}
            >
              {filterOptions.warehouses.map((warehouse: any) => (
                <Option key={warehouse.id} value={warehouse.id}>
                  {warehouse.description}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={6}>
            <Select
              placeholder="Utente"
              allowClear
              style={{ width: '100%' }}
              value={filters.user_id}
              onChange={(value) => handleFilterChange('user_id', value)}
            >
              {filterOptions.users.map((user: any) => (
                <Option key={user.id} value={user.id}>
                  {user.username}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={12}>
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Data Inizio', 'Data Fine']}
              onChange={handleDateRangeChange}
              value={
                filters.start_date && filters.end_date
                  ? [dayjs(filters.start_date), dayjs(filters.end_date)]
                  : null
              }
            />
          </Col>

          <Col span={12}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadMovements}
              >
                Aggiorna
              </Button>
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
              >
                Pulisci Filtri
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={movements}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.page_size,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Totale ${total} movimenti`,
            onChange: (page, pageSize) => {
              setPagination({
                ...pagination,
                page,
                page_size: pageSize || pagination.page_size
              });
            }
          }}
          scroll={{ x: 1500 }}
        />
      </Card>
    </div>
  );
};

export default ProductMovements;
